import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The rest of Algebra I: exponential change, sequences, rational expressions
 * and absolute value.
 *
 * Exponential growth is the one most worth teaching well. A student who
 * genuinely grasps that multiplicative change always overtakes additive change
 * has something that will serve them in finance, epidemiology and computing
 * long after the algebra itself has faded.
 */
export const ALGEBRA_ONE_LESSONS: Lesson[] = [
  lesson({
    id: 'exponential-functions-01',
    unitId: 'unit-growth',
    conceptId: 'exponential-functions',
    title: 'Exponential Growth and Decay',
    summary: 'Change that multiplies rather than adds — and why it always overtakes a straight line eventually.',
    objectives: [
      'Tell exponential change from linear change',
      'Apply repeated percentage growth and decay',
      'Read the meaning of the starting value and the growth factor',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two ways to grow',
        blocks: [
          text('Two savings accounts, each starting with £1000. The first adds £100 a year. The second grows by 10% a year.'),
          text('After one year both hold £1100 — and they never agree again.'),
          table(['Year', 'Adds £100', 'Grows 10%'], [
            ['0', '£1000', '£1000'],
            ['1', '£1100', '£1100'],
            ['5', '£1500', '£1611'],
            ['10', '£2000', '£2594'],
            ['30', '£4000', '£17,449'],
          ]),
          text('The difference is not the rate. It is that one adds a fixed amount while the other multiplies, so the amount added grows every single year.'),
        ],
      },
      intuition: {
        title: 'Multiplying, repeatedly',
        blocks: [
          text('**Linear** change adds the same amount each step. **Exponential** change multiplies by the same factor each step.'),
          math('y = a \\cdot b^{x}'),
          list([
            '**a** is the starting value — what you have at x = 0.',
            '**b** is the growth factor, the number you multiply by each step.',
          ]),
          insight('The growth factor comes straight from the percentage, exactly as the multipliers did in Grade 7. Growing by 10% means b = 1.10; shrinking by 10% means b = 0.90. Above 1 is growth, below 1 is decay, and b is never negative.'),
          warning('Applying the percentage to the *original* amount every time is the classic error, and it quietly turns the problem into a linear one. Each step applies to the new total, which is why the amounts added keep increasing.', 'Growth on growth'),
          text('The consequence worth internalising is this: **exponential growth always overtakes linear growth eventually**, whatever the numbers. A line adding a million a year is beaten in the end by something growing at 1% a year, because the line adds a constant and the curve adds a percentage of an ever-larger amount.'),
          insight('Decay works identically with b below 1. Halving is b = 0.5; a radioactive substance losing 3% a year has b = 0.97. Decay approaches zero without ever quite arriving.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -1,
              max: 11,
              points: [
                { x: 0, y: 1 },
                { x: 1, y: 2 },
                { x: 2, y: 4 },
                { x: 3, y: 8 },
                { x: 4, y: 10, label: 'doubling' },
              ],
              lines: [{ slope: 2, intercept: 1, label: 'linear', dashed: true }],
            },
            'A doubling sequence against a straight line. The line is ahead at first, and the curve passes it and never looks back.',
          ),
          tip('The early part of the graph is why exponential growth is so often underestimated in real life. For several steps it looks slower than the line, and by the time it obviously is not, it is far too late to be surprised.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A car worth £18,000 loses 15% of its value each year. What is it worth after 4 years?'),
          steps([
            { label: 'Losing 15% means keeping 85%.', latex: 'b = 0.85' },
            { label: 'Write the rule.', latex: 'y = 18000 \\times 0.85^{x}' },
            { label: 'Substitute x = 4.', latex: '18000 \\times 0.85^{4}' },
            { label: 'Work it out.', latex: '\\approx £9396' },
          ]),
          text('Losing 15% four times is not losing 60%. It is losing rather less — about 48% — because each year’s loss is taken from a smaller amount than the last. That gap is the whole difference between the two kinds of change.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('exponential-functions', 4)] },
      independent: { title: 'On your own', blocks: [practice('exponential-functions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('exponential-functions', 3, 'Telling multiplicative change from additive.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Linear change adds a fixed amount; exponential change multiplies by a fixed factor.',
            'y = a·bˣ, where a is the start and b the growth factor.',
            'b above 1 is growth, below 1 is decay, and it comes straight from the percentage.',
            'Each step applies to the new total, not the original.',
            'Exponential growth always overtakes linear growth eventually.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'sequences-01',
    unitId: 'unit-growth',
    conceptId: 'sequences',
    title: 'Arithmetic and Geometric Sequences',
    summary: 'Patterns of numbers, and the two ways a pattern can carry on: by adding, or by multiplying.',
    objectives: [
      'Tell an arithmetic sequence from a geometric one',
      'Find the nth term of an arithmetic sequence',
      'Continue a geometric sequence',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two kinds of pattern',
        blocks: [
          text('Continue each of these:'),
          math('3,\\ 7,\\ 11,\\ 15,\\ \\ldots \\qquad 3,\\ 6,\\ 12,\\ 24,\\ \\ldots'),
          text('The first adds 4 each time; the second doubles. Those are the only two patterns this lesson is about, and they are the sequence versions of the two kinds of change from the last one.'),
        ],
      },
      intuition: {
        title: 'Adding or multiplying',
        blocks: [
          list([
            'An **arithmetic** sequence adds the same amount each time — the **common difference**, d.',
            'A **geometric** sequence multiplies by the same amount each time — the **common ratio**, r.',
          ]),
          insight('To tell which you have, do both tests. Subtract consecutive terms; divide consecutive terms. Whichever gives the same answer every time is the rule — and if neither does, it is neither kind.'),
          text('An arithmetic sequence is a linear function on the whole numbers, so its rule looks familiar:'),
          math('a_{n} = a_{1} + (n - 1)d'),
          warning('That n − 1 is where this goes wrong. The *first* term has had no differences added yet, so reaching the nth term takes n − 1 steps, not n. Test your rule by putting n = 1 into it and checking you get the first term back.', 'The off-by-one'),
          text('A geometric sequence is an exponential function on the whole numbers, with the same structure:'),
          math('a_{n} = a_{1} \\cdot r^{\\,n-1}'),
          insight('So sequences are not a new topic. They are linear and exponential functions with the inputs restricted to 1, 2, 3, … — which is why everything from the last two units carries straight over.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Sequence', 'Test', 'Kind', 'Rule'], [
            ['5, 8, 11, 14', 'difference 3', 'arithmetic', 'aₙ = 5 + 3(n − 1)'],
            ['2, 6, 18, 54', 'ratio 3', 'geometric', 'aₙ = 2 · 3ⁿ⁻¹'],
            ['20, 15, 10, 5', 'difference −5', 'arithmetic', 'aₙ = 20 − 5(n − 1)'],
            ['64, 32, 16, 8', 'ratio ½', 'geometric', 'aₙ = 64 · (½)ⁿ⁻¹'],
            ['1, 4, 9, 16', 'neither constant', 'neither', 'aₙ = n²'],
          ]),
          tip('That last row is a reminder not to force the question. Plenty of interesting sequences are neither arithmetic nor geometric, and the honest answer is to say so.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A sequence begins 7, 11, 15, 19. Find its 30th term.'),
          steps([
            { label: 'Test for a common difference.', latex: '11 - 7 = 4,\\quad 15 - 11 = 4' },
            { label: 'Arithmetic, with d = 4 and first term 7.', latex: 'a_{n} = 7 + 4(n - 1)' },
            { label: 'Check with n = 1.', latex: '7 + 4(0) = 7 \\quad\\text{✓}' },
            { label: 'Substitute n = 30.', latex: '7 + 4(29) = 7 + 116 = 123' },
          ]),
          text('The check at n = 1 costs nothing and catches the off-by-one immediately. Using 4 × 30 would have given 127, which is the 31st term.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('sequences', 4)] },
      independent: { title: 'On your own', blocks: [practice('sequences', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('sequences', 3, 'Later terms, where the off-by-one matters most.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Arithmetic adds a common difference; geometric multiplies by a common ratio.',
            'Test both: subtract consecutive terms, and divide them.',
            'aₙ = a₁ + (n − 1)d for arithmetic; aₙ = a₁·rⁿ⁻¹ for geometric.',
            'It is n − 1, not n — check by putting n = 1 in.',
            'Some sequences are neither, and saying so is the right answer.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'rational-expressions-01',
    unitId: 'unit-rational-algebra',
    conceptId: 'rational-expressions',
    title: 'Rational Expressions',
    summary: 'Fractions whose top and bottom are polynomials — where only factors may cancel, never terms.',
    objectives: [
      'Simplify a rational expression by factorising and cancelling',
      'Explain why terms cannot be cancelled',
      'Identify values that make a denominator zero',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Algebraic fractions',
        blocks: [
          text('A **rational expression** is a fraction with polynomials on top and bottom:'),
          math('\\frac{x^{2} + 5x + 6}{x^{2} + 4x + 4}'),
          text('Everything you know about numerical fractions applies. Cancel common factors, find common denominators to add, multiply straight across. The one new hazard is what counts as a factor.'),
        ],
      },
      intuition: {
        title: 'Factors cancel; terms do not',
        blocks: [
          text('This is the error that defines the topic, so it is worth meeting head-on. Consider:'),
          math('\\frac{x + 3}{3}'),
          text('It is enormously tempting to cancel the 3s and write x. Test it with x = 6: the expression is 9/3 = 3, and x is 6. Not the same.'),
          insight('Only **factors** cancel — things being *multiplied*. In (x + 3)/3 the 3 is being *added* to x, so it is a term, and terms cannot be removed one at a time.'),
          text('Compare with a case where cancelling is legitimate:'),
          math('\\frac{3x}{3} = x'),
          text('Here the 3 multiplies x, so it is a factor and it goes. The two expressions look almost identical and behave completely differently.'),
          warning('The rule in one sentence: if you cannot see the whole numerator as a product, nothing in it may be cancelled. Factorise first, then cancel entire brackets.', 'The test to apply'),
          text('One more thing a numerical fraction did not require. A denominator may not be zero, and with an x in it you must say which values are excluded:'),
          math('\\frac{x + 2}{x - 5} \\quad\\text{is undefined at } x = 5'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Expression', 'Cancel?', 'Why'], [
            ['6x / 3', 'yes → 2x', '3 is a factor'],
            ['(x + 6) / 3', 'no', '6 is a term, not a factor'],
            ['(3x + 6) / 3', 'yes → x + 2', 'factorise first: 3(x + 2)/3'],
            ['(x + 2)(x − 1) / (x + 2)', 'yes → x − 1', 'a whole bracket is a factor'],
            ['(x² + 2) / x', 'no', 'the top is a sum'],
          ]),
          insight('The third row is the pattern to internalise. Nothing could be cancelled as written; after factorising, everything could. Factorising is not a preliminary — it is the operation that reveals what the factors are.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Simplify (x² + 5x + 6)/(x² + 4x + 4), and say where it is undefined.'),
          steps([
            { label: 'Factorise the numerator.', latex: '(x + 2)(x + 3)' },
            { label: 'Factorise the denominator.', latex: '(x + 2)(x + 2)' },
            { label: 'Cancel the common bracket — a genuine factor.', latex: '\\frac{x + 3}{x + 2}' },
            { label: 'The denominator is zero at x = −2.', latex: 'x \\ne -2' },
          ]),
          tip('Note what is *not* done at the end: the x’s in (x + 3)/(x + 2) do not cancel. They are terms, and the expression is finished.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('rational-expressions', 4)] },
      independent: { title: 'On your own', blocks: [practice('rational-expressions', 4)] },
      challenge: { title: 'Something harder', blocks: [practice('rational-expressions', 3, 'Expressions needing factorising before anything cancels.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A rational expression is a fraction of polynomials.',
            'Only factors cancel — things being multiplied, never added.',
            'Factorise top and bottom first, then cancel whole brackets.',
            'A denominator may never be zero, so state the excluded values.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'absolute-value-01',
    unitId: 'unit-rational-algebra',
    conceptId: 'absolute-value',
    title: 'Absolute Value Equations and Inequalities',
    summary: 'Distance from zero — which is why these have two answers, or none, and why one inequality splits and the other does not.',
    objectives: [
      'Interpret absolute value as distance from zero',
      'Solve an absolute value equation, finding both cases',
      'Recognise an equation with no solution',
      'Solve |x| < k as a band and |x| > k as two pieces',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'How far, not which way',
        blocks: [
          text('The **absolute value** of a number is its distance from zero, and distance has no direction.'),
          math('|7| = 7 \\qquad |-7| = 7 \\qquad |0| = 0'),
          text('That one idea explains everything odd about these equations: why they usually have two answers, and why some have none at all.'),
        ],
      },
      intuition: {
        title: 'Two cases, always',
        blocks: [
          text('Ask which numbers are 7 away from zero. There are two, one in each direction — so:'),
          math('|x| = 7 \\quad\\Longrightarrow\\quad x = 7 \\text{ or } x = -7'),
          insight('Whenever the thing inside the bars equals a positive number, there are two cases: the inside is that number, or the inside is its negative. Solve both.'),
          text('With an expression inside, both cases still get solved in full:'),
          math('|x - 3| = 5 \\quad\\Longrightarrow\\quad x - 3 = 5 \\text{ or } x - 3 = -5'),
          text('giving x = 8 or x = −2. Check on a number line: both are exactly 5 away from 3, which is what the equation was asking for all along.'),
          warning('Solving only the first case is the standard error, and it produces a real solution — just not all of them. An absolute value equation is not finished until both cases are done.', 'Half an answer'),
          text('And the case that surprises people:'),
          math('|x| = -4 \\quad\\Longrightarrow\\quad \\text{no solution}'),
          insight('A distance cannot be negative, so nothing satisfies this. That is not a trick — it is the definition doing its job, and "no solution" is a complete and correct answer.'),
          text('Replace the equals sign with an inequality and the same distance reading answers it — but the two directions give shapes that are not merely different, they are opposite.'),
          math('|x| < 5 \quad\Longrightarrow\quad -5 < x < 5'),
          text('"Less than 5 from zero" is the points *near* zero: one unbroken band, and both conditions have to hold at once.'),
          math('|x| > 5 \\quad\\Longrightarrow\\quad x < -5 \\;\\text{ or }\\; x > 5'),
          insight('"More than 5 from zero" is the points *far* from zero, and there is no way to be far from zero in both directions at the same time. So this one is two separate pieces, joined by "or" — and no single chain like −5 > x > 5 can express it, because that chain describes nothing at all.'),
          warning('Both conditions of the band are genuinely different statements, not one statement with a sign changed. |x| < 5 gives x < 5 *and* x > −5. Writing the second as x < −5 keeps the number and loses the meaning, and the resulting "answer" is empty.', 'The second case flips'),
          text('Everything else carries over unchanged. With an expression inside the bars, both cases are solved in full, and a negative on the right still says something absolute: |x| < −2 has no solutions, while |x| > −2 is true for every number there is.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'number-line', from: -8, to: 8, step: 1, marks: [{ at: -5, label: '|−5| = 5' }, { at: 0, label: '0' }, { at: 5, label: '|5| = 5' }] },
            'Both −5 and 5 are five units from zero. That symmetry is the reason for the two cases.',
          ),
          table(['Equation', 'Cases', 'Solutions'], [
            ['|x| = 9', 'x = 9, x = −9', 'two'],
            ['|x + 1| = 4', 'x + 1 = ±4', '3 and −5'],
            ['|x| = 0', 'x = 0', 'one'],
            ['|x| = −2', 'none possible', 'none'],
          ]),
          text('The inequalities are the same two points with everything between them either kept or thrown away:'),
          figure(
            { kind: 'number-line', from: -8, to: 8, step: 1, marks: [{ at: -5, label: '−5' }, { at: 5, label: '5' }] },
            '|x| < 5 keeps the inside — the single band between −5 and 5. |x| > 5 keeps the two outsides instead, and nothing in the middle.',
          ),
          table(['Inequality', 'Reads as', 'Shape'], [
            ['|x| < 5', '−5 < x < 5', 'one band, joined by "and"'],
            ['|x| > 5', 'x < −5 or x > 5', 'two pieces, joined by "or"'],
            ['|x − 2| ≤ 3', '−1 ≤ x ≤ 5', 'a band, endpoints included'],
            ['|x| > −2', 'every number', 'the whole line'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve |2x − 1| = 9.'),
          steps([
            { label: 'Split into two cases.', latex: '2x - 1 = 9 \\quad\\text{or}\\quad 2x - 1 = -9' },
            { label: 'Solve the first.', latex: '2x = 10 \\Rightarrow x = 5' },
            { label: 'Solve the second.', latex: '2x = -8 \\Rightarrow x = -4' },
            { label: 'Check both in the original.', latex: '|9| = 9 \\quad |-9| = 9' },
          ]),
          text('Both check out, and both are needed. Note that the second case is solved completely, not just up to the sign — the −9 goes through the whole rearrangement.'),
          text('Now the inequality version: solve |2x − 1| ≤ 9.'),
          steps([
            { label: 'Less than, so it is a band: the inside lies between the two.', latex: '-9 \le 2x - 1 \le 9' },
            { label: 'Add 1 everywhere. Every part of the chain gets the same treatment.', latex: '-8 \le 2x \le 10' },
            { label: 'Divide everywhere by 2.', latex: '-4 \le x \le 5' },
          ]),
          text('The endpoints are exactly the two solutions of the equation, which is the useful connection: solving the equation locates the boundary, and the inequality then decides whether to keep the inside or the outside.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('absolute-value', 4)] },
      independent: { title: 'On your own', blocks: [practice('absolute-value', 4)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('absolute-value', 3, 'Inequalities that split, and equations with one solution or none at all.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Absolute value is distance from zero, so it is never negative.',
            '|x| = k with k positive gives two cases: x = k and x = −k.',
            'Solve both cases completely.',
            '|x| = 0 has exactly one solution; |x| = a negative has none.',
            '|x| < k is one band and needs "and"; |x| > k is two pieces and needs "or".',
            'The second case reverses the inequality as well as the sign of the number.',
          ]),
        ],
      },
    },
  }),
];
